import React, { type HTMLAttributes, type ReactElement } from 'react';

import classnames from 'classnames';

import { TOOLTIP } from '../../../dataTestIds';
import { Icon } from '../Icon';
import { Tooltip, type TooltipProps } from '../Tooltip';
import { Label } from '../Typography';

import styles from './FieldLabel.module.scss';

export type FieldLabelProps = {
    label: string;
    htmlFor?: string;
    /**
     * An informatory tooltip that is displayed when hovering over the label icon.
     *
     * If a function is passed, it will be called with the props object as argument:
     * ```
     * <FieldLabel label="Label" tooltip={(props) => <MyCustomTooltip title="Tooltip" {...props} />
     * ```
     */
    tooltip?: string | ((props: { children: ReactElement; id?: string }) => ReactElement);
    tooltipPlacement?: TooltipProps['placement'];
    tooltipId?: string;
    required?: boolean;
    containerClassName?: string;
} & HTMLAttributes<HTMLLabelElement>;

export const FieldLabel = ({
    label,
    htmlFor,
    children,
    tooltip,
    tooltipPlacement,
    tooltipId,
    required,
    containerClassName,
    ...rest
}: FieldLabelProps) => {
    const icon = (
        <Icon
            data-testid={TOOLTIP(tooltipId!)}
            className={styles.icon}
            name="s-icon-circle-info"
            tabIndex={0}
        />
    );
    return (
        <Label>
            <span className={classnames(styles.labelContainer, containerClassName)}>
                <label htmlFor={htmlFor} {...rest}>
                    <Label>
                        {label}
                        {required && '*'}
                    </Label>
                </label>
                {typeof tooltip === 'function'
                    ? tooltip({ children: icon, id: tooltipId })
                    : tooltip && (
                          <Tooltip
                              trigger={['hover', 'focus']}
                              title={tooltip}
                              id={tooltipId}
                              placement={tooltipPlacement}
                          >
                              {icon}
                          </Tooltip>
                      )}
            </span>
            {children}
        </Label>
    );
};
